/**
 * Boot (DESIGN-client §7): the logo over the same room as the entry menu, a bar driven by the real
 * asset bundle, and then it continues by itself.
 *
 * There is no "Deal me in" tap any more. It bought one thing — a user gesture to unlock audio with
 * — and cost a press on every single launch, which is a whole screen of friction in front of a
 * game nobody has agreed to play yet. The audio unlock now happens on the first gesture the player
 * makes for their own reasons (`audio/install.ts`), so the boot screen has no work left to justify
 * asking for one, and it hands over as soon as the runtime and the identity have settled.
 *
 * What is still waited on, and why:
 * - the runtime (`useRuntime`), because nothing downstream can connect without it;
 * - identity, settled either way — a resolved player or a failed lookup that means "guest";
 * - the boot bundle, with a 3s cap so a slow CDN delays the hand-over but cannot block it;
 * - a ~420ms floor, so the bar is seen to finish rather than flashing past.
 *
 * The button only comes back when none of that has happened after 8 seconds, which is the one case
 * where a player is looking at a screen with nothing to do.
 *
 * The progress bar is driven by the `boot` asset bundle (`scene/assets/manifest.ts`): ui@1x, the
 * MSDF fonts and deck@1x, which is everything the first interactive frame needs. The `table`
 * bundle (deck@2x, ledger, vfx, sfx) is kicked off later, from the scene's first frame, so it
 * competes with nothing the player is waiting on.
 *
 * The import is dynamic on purpose: `manifest.ts` pulls in Pixi's `Assets`, and a static import
 * here would put the scene chunk back on the boot path that C-T25 exists to keep clear.
 */
import { useEffect, useRef, useState } from 'react';
import { useStore, store } from '../../store/store';
import { screenAfterBoot } from '../routing';
import { audioHooks, useRuntime } from '../runtime';
import { Button } from '../ui';
import { SiteLink } from '../ui/SiteLink';
import { EntryAtmosphere, EntryLogo } from './EntryMenu';

/** Shortest time the boot screen is allowed to exist, so the bar completes rather than blinks. */
const MIN_DWELL_MS = 420;
/** How long the asset bundle gets to finish before the hand-over stops waiting for it. */
const ASSET_GRACE_MS = 3000;
/** How long a boot may hang before the player is offered a way through it by hand. */
const STALL_MS = 8000;

export function dealMeIn(): void {
  const rt = store.getState();
  const hooks = audioHooks();
  try {
    hooks?.unlock?.();
  } catch {
    /* no audio engine yet */
  }
  const bridge = (window as unknown as { __spades?: { bridge?: { encourageHardwareAcceleration(): Promise<void>; lockOrientation(): Promise<void> } } }).__spades?.bridge;
  void bridge?.encourageHardwareAcceleration().catch(() => {});
  void bridge?.lockOrientation().catch(() => {});
  rt.setPresentation({ screen: screenAfterBoot({ tutorialDone: rt.settings.tutorialDone, search: location.search }) });
}

/** A timer that flips a flag once; `false` until `ms` has passed since mount. */
function useAfter(ms: number): boolean {
  const [passed, setPassed] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setPassed(true), ms);
    return () => window.clearTimeout(id);
  }, [ms]);
  return passed;
}

export function Boot() {
  const rt = useRuntime();
  const progress = useStore((s) => s.presentation.bootProgress);
  const me = useStore((s) => s.session.me);
  const error = useStore((s) => s.session.error);

  // Stage the boot bundle once per page and publish its progress to the bar.
  useEffect(() => {
    let alive = true;
    void import('../../scene/assets/manifest')
      .then(({ loadBootBundle }) =>
        loadBootBundle((p) => {
          if (alive) store.getState().setPresentation({ bootProgress: p });
        }),
      )
      .catch((err: unknown) => console.warn('[boot] asset staging failed', err));
    return () => {
      alive = false;
    };
  }, []);

  const dwelt = useAfter(MIN_DWELL_MS);
  const assetGrace = useAfter(ASSET_GRACE_MS);
  const stalled = useAfter(STALL_MS);

  const ready = rt != null;
  // Identity has *settled*, not succeeded: a failed lookup is an answer, and its answer is "guest".
  const settled = me != null || error != null;
  const assetsIn = progress >= 1 || assetGrace;
  const clear = ready && settled && assetsIn && dwelt;

  // Latched in a ref, not state: the effect re-runs whenever any of its inputs move, and a second
  // `continueFromBoot` would re-run the bridge calls and re-decide the route from a store that has
  // already left the boot screen.
  const wentRef = useRef(false);
  useEffect(() => {
    if (wentRef.current || !clear || !rt) return;
    wentRef.current = true;
    continueFromBoot(rt.bridge);
  }, [clear, rt]);

  const status = !ready ? 'Connecting to the table' : me ? `Welcome, ${me.name}` : error ? 'Playing as a guest' : 'Getting your seat';
  const pct = Math.round(Math.min(1, Math.max(0, progress)) * 100);

  return (
    /*
     * `entry boot`, not `boot`. The frame, the room and the lockup are the entry menu's, so the
     * wordmark is in the same place on both screens and arriving at the menu swaps a bar for two
     * buttons rather than re-composing the brand in front of the player. The empty top row is the
     * identity pill's, and it is here for exactly that reason: it holds the height.
     */
    <div className="entry boot" data-testid="screen-boot">
      <EntryAtmosphere />

      <div className="entry__top" />

      <div className="entry__content">
        <EntryLogo tagline="Four seats · two teams · talk your trash" />

        <div className="boot__inner">
          <div className="boot__bar" aria-label="Loading" role="progressbar" aria-valuenow={pct}>
            {/* No minimum width any more: `bootProgress` is driven by the real boot bundle, so a
                bar that has not moved means loading has not started, and that is worth seeing. */}
            <div className="boot__fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="boot__status" data-testid="boot-status">
            {status}
          </div>
          {/* Only ever seen by a boot that did not finish. It keeps the `deal-me-in` id because it
              is still the same control: the manual way past this screen. */}
          {stalled && !clear && (
            <Button
              variant="primary"
              size="lg"
              block
              disabled={!ready}
              testId="deal-me-in"
              onClick={() => rt && continueFromBoot(rt.bridge)}
            >
              Deal me in
            </Button>
          )}
        </div>
      </div>

      <div className="entry__foot">
        <SiteLink className="entry__foot-link" path="/rules">
          How to play
        </SiteLink>
        <span className="entry__foot-sep" aria-hidden>
          ·
        </span>
        <SiteLink className="entry__foot-link" path="/privacy">
          Privacy
        </SiteLink>
        <span className="entry__foot-sep" aria-hidden>
          ·
        </span>
        <SiteLink className="entry__foot-link" path="/terms">
          Terms
        </SiteLink>
      </div>
    </div>
  );
}

function continueFromBoot(bridge: { encourageHardwareAcceleration(): Promise<void>; lockOrientation(): Promise<void> }): void {
  const st = store.getState();
  try {
    audioHooks()?.unlock?.();
  } catch {
    /* no engine */
  }
  void bridge.encourageHardwareAcceleration().catch(() => {});
  void bridge.lockOrientation().catch(() => {});
  st.setPresentation({ screen: screenAfterBoot({ tutorialDone: st.settings.tutorialDone, search: location.search }) });
}
